/**
 * Inicialización Sentry — frontend (Sprint 7 B4).
 *
 * Sentry SOLO se activa cuando el bundle se ejecuta en MODE=production
 * (Vite expone `import.meta.env.PROD` true en ese caso). En dev/test el
 * SDK queda completamente inactivo: sin script, sin tráfico, sin cupo
 * gastado.
 *
 * Decisiones de configuración (todas razonadas en el brief Sprint 7 B4):
 *
 * 1. **Activación condicional**: `import.meta.env.PROD` es el flag
 *    canónico de Vite — true solo en builds de producción.
 * 2. **DSN público por diseño**: el DSN del frontend va embebido en el
 *    bundle JS (lo lee cualquier user). Sentry está diseñado para esto:
 *    el DSN sirve para enviar eventos, no para acceder a la cuenta.
 * 3. **tracesSampleRate=0.1**: 10% de pageviews con performance traces.
 *    Mismo ratio que el backend.
 * 4. **Session Replay condicional**:
 *    - `replaysSessionSampleRate=0.0`: NO grabar sesiones sin error.
 *    - `replaysOnErrorSampleRate=1.0`: SÍ grabar el contexto cuando
 *      ocurre un error — el replay (60s antes + 30s después) llega
 *      a Sentry junto con el stack trace. Es la diferencia entre
 *      "tengo un error" y "veo qué hacía el user cuando pasó".
 * 5. **`maskAllText=true`, `maskAllInputs=true`, `blockAllMedia=true`**
 *    (R6b GDPR): la app muestra DATOS DE SALUD (FC máx, VAM, ritmos en
 *    "Mis datos") y el email (Home). Con el replay grabando el DOM ante
 *    cada error, el texto visible NO debe llegar a Sentry en claro →
 *    enmascarado total. No cambiamos CUÁNDO se graba (sampling), solo
 *    QUÉ se graba (enmascarado).
 * 6. **`beforeSend` filter + scrubbing PII** (R6b): descarta errores de
 *    extensiones de navegador (Grammarly, AdBlockers, etc.) Y scrubbea
 *    claves sensibles (token/password/email/hr/vam/secret/...) en
 *    `extra`/`contexts`/`request`/`breadcrumbs` → defensa en profundidad
 *    espejo del scrubbing del backend (S14.1). NOTA: el SDK de JS NO
 *    captura variables locales de los stack traces (a diferencia de
 *    Python), así que aquí NO hay análogo a `include_local_variables`.
 * 7. **`sendDefaultPii=false`** explícito: sin IP/cookies/headers auto.
 *
 * Wiring: llamar `initSentry()` ANTES de `ReactDOM.createRoot` en
 * `main.tsx`. Razón: Sentry hook se monta a nivel de window para
 * capturar errores sincrónicos del bootstrap React.
 */

import * as Sentry from "@sentry/react";

/** Sampling rate de performance traces. 10% — mismo ratio que el backend. */
const TRACES_SAMPLE_RATE = 0.1;

// =====================================================================
// Scrubbing de PII (R6b — espejo del backend S14.1)
// =====================================================================
const SCRUBBED = "[scrubbed]";
// Profundidad máxima del recorrido recursivo (anti-circular).
const MAX_SCRUB_DEPTH = 12;
// Términos distintivos: match por SUBSTRING (case-insensitive).
const SCRUB_SUBSTRINGS = [
  "token", // access_token, refresh_token, csrf_token...
  "password",
  "passwd",
  "secret", // client_secret...
  "authorization",
  "api_key",
  "apikey",
  "credential",
  "email",
];
// Claves cortas/ambiguas (datos de salud): match por COMPONENTE de palabra
// (no substring) para no tocar "threshold"/"thread"/"duration".
const SCRUB_WORD_COMPONENTS = ["hr", "vam"];

function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  if (SCRUB_SUBSTRINGS.some((sub) => k.includes(sub))) return true;
  const components = k.split(/[^a-z0-9]+/);
  return SCRUB_WORD_COMPONENTS.some((w) => components.includes(w));
}

/** Recorre dicts/arrays IN PLACE reemplazando valores de claves sensibles. */
function scrubDeep(value: unknown, depth = 0): void {
  if (depth > MAX_SCRUB_DEPTH || value === null || typeof value !== "object") {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) scrubDeep(item, depth + 1);
    return;
  }
  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (isSensitiveKey(key)) {
      obj[key] = SCRUBBED;
    } else {
      scrubDeep(obj[key], depth + 1);
    }
  }
}

/**
 * Scrubbea PII de un evento Sentry IN PLACE (extra/contexts/request/
 * breadcrumbs) y lo devuelve. Exportado para test aislado.
 */
export function scrubSentryEvent<T extends Record<string, unknown>>(
  event: T,
): T {
  for (const field of ["extra", "contexts", "request", "breadcrumbs"]) {
    if (event[field] != null) scrubDeep(event[field], 0);
  }
  return event;
}

export function initSentry(): void {
  // No-op en dev/test/preview. `PROD` es true solo en build de prod.
  if (!import.meta.env.PROD) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
  if (!dsn) {
    console.warn(
      "[sentry] VITE_SENTRY_DSN_FRONTEND no configurado en producción. " +
        "Errores no se reportarán.",
    );
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || "dev",

    tracesSampleRate: TRACES_SAMPLE_RATE,

    // R6b (GDPR): sin IP/cookies/headers auto-adjuntados (ya es el default
    // en @sentry/react v8; explícito para documentar la intención).
    sendDefaultPii: false,

    // Session Replay: solo cuando hay error (ver decisión 4 en el doc).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      // R6b (GDPR): enmascarar TODO el texto/inputs/media del DOM en los
      // replays — la app muestra FC/VAM/ritmos/email que NO deben llegar
      // a Sentry en claro. Solo cambia QUÉ se graba, no CUÁNDO.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    beforeSend(event) {
      // 1) Filtrar errores de extensiones de navegador (Grammarly, ad
      //    blockers, password managers, etc.) — no son bugs nuestros.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;
      if (frames?.some((f) => f.filename?.includes("extension://"))) {
        return null;
      }
      // 2) R6b (GDPR): scrubbing de PII (token/email/hr/vam/... → [scrubbed])
      //    sobre extra/contexts/request/breadcrumbs. Defensa en profundidad
      //    junto al enmascarado del replay. Muta in place → devolvemos el
      //    mismo evento (tipo Sentry correcto).
      scrubSentryEvent(event as unknown as Record<string, unknown>);
      return event;
    },
  });
}
